import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  Users,
  Inbox,
  LayoutDashboard,
  ArrowLeft,
  Plus,
  Check,
  MessageSquare,
  Image as ImageIcon,
  Upload,
  Trash2,
} from "lucide-react";
import { adminApi, getToken, setToken, clearToken } from "@/lib/adminAuth";
import { APPLIANCES } from "@/lib/appliances";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [view, setView] = useState("checking"); // checking | login | dashboard
  const [user, setUser] = useState(null);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/auth/me");
      setUser(data);
      setView("dashboard");
    } catch {
      clearToken();
      setView("login");
    }
  }, []);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const sid = hash.split("session_id=")[1].split("&")[0];
      (async () => {
        try {
          const { data } = await adminApi.post("/auth/google-session", { session_id: sid });
          setToken(data.token);
          window.history.replaceState(null, "", window.location.pathname);
          await loadMe();
          toast.success("Signed in with Google");
        } catch (e) {
          window.history.replaceState(null, "", window.location.pathname);
          toast.error(e?.response?.data?.detail || "Google sign-in failed");
          setView("login");
        }
      })();
      return;
    }
    if (getToken()) loadMe();
    else setView("login");
  }, [loadMe]);

  const logout = async () => {
    try { await adminApi.post("/auth/logout"); } catch {}
    clearToken();
    setUser(null);
    setView("login");
  };

  if (view === "checking") {
    return (
      <div className="min-h-screen bg-[#0C1C2E] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#F26A21]" size={32} />
      </div>
    );
  }

  if (view === "login") return <AdminLogin onLoggedIn={loadMe} />;

  return <AdminDashboard user={user} onLogout={logout} />;
}

function AdminLogin({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const inputClass =
    "w-full h-14 bg-[#0C1C2E] border border-[#223851] rounded-xl px-4 text-white placeholder:text-[#6B7E92] focus:border-[#F26A21] focus:outline-none focus:ring-2 focus:ring-[#F26A21]/30 transition-all";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await adminApi.post("/auth/login", { email, password });
      setToken(data.token);
      await onLoggedIn();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0C1C2E] flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(242,106,33,0.10),transparent_55%)]" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-[#8CA0B4] hover:text-[#F26A21] transition-colors mb-8 text-sm" data-testid="admin-back-home">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="rounded-3xl bg-[#122539] border border-[#223851] p-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-11 h-11 rounded-xl bg-white flex items-center justify-center overflow-hidden p-0.5">
              <img src="/asa-logo.jpeg" alt="ASA" className="w-full h-full object-contain" />
            </span>
            <div>
              <h1 className="font-heading font-bold text-xl text-white">Admin</h1>
              <p className="text-xs text-[#8CA0B4]">ASA Home Appliance Services</p>
            </div>
          </div>

          <form onSubmit={submit} data-testid="admin-login-form" className="space-y-4">
            <div>
              <label className="overline text-[#8CA0B4] block mb-2">Email</label>
              <input data-testid="admin-email" type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@..." />
            </div>
            <div>
              <label className="overline text-[#8CA0B4] block mb-2">Password</label>
              <input data-testid="admin-password" type="password" className={inputClass} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full h-14 bg-[#F26A21] text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#DE5C15] transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#223851]" />
            <span className="text-xs text-[#6B7E92]">or</span>
            <div className="flex-1 h-px bg-[#223851]" />
          </div>

          <button onClick={googleLogin} data-testid="admin-google-login" className="w-full h-14 bg-white text-[#0C1C2E] rounded-full font-semibold flex items-center justify-center gap-3 hover:bg-[#EEF2F6] transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminDashboard({ user, onLogout }) {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [gallery, setGallery] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const [s, e, c, f, g] = await Promise.all([
        adminApi.get("/admin/stats"),
        adminApi.get("/admin/enquiries"),
        adminApi.get("/admin/customers"),
        adminApi.get("/admin/feedback"),
        adminApi.get("/gallery"),
      ]);
      setStats(s.data);
      setEnquiries(e.data);
      setCustomers(c.data);
      setFeedback(f.data);
      setGallery(g.data);
    } catch {
      toast.error("Failed to load admin data");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const TABS = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "enquiries", label: "Enquiries", icon: Inbox },
    { id: "customers", label: "Customers", icon: Users },
    { id: "feedback", label: "Feedback", icon: MessageSquare },
    { id: "gallery", label: "Gallery", icon: ImageIcon },
  ];

  return (
    <div className="min-h-screen bg-[#0C1C2E] text-white">
      <header className="border-b border-[#223851] bg-[#0A1626]">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-xl bg-white flex items-center justify-center overflow-hidden p-0.5">
              <img src="/asa-logo.jpeg" alt="ASA" className="w-full h-full object-contain" />
            </span>
            <span className="font-heading font-bold">ASA Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-[#8CA0B4]">{user?.email}</span>
            <Link to="/" className="text-sm text-[#8CA0B4] hover:text-[#F26A21] transition-colors">View site</Link>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm bg-white/5 border border-[#223851] rounded-full px-4 py-2 hover:border-[#F26A21] hover:text-[#F26A21] transition-colors">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-2 mb-8 border-b border-[#223851]">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-[#F26A21] text-[#F26A21]" : "border-transparent text-[#8CA0B4] hover:text-white"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div data-testid="admin-overview">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                ["Total customers", stats?.total_customers ?? "—"],
                ["Total enquiries", stats?.total_enquiries ?? "—"],
                ["Feedback received", stats?.total_feedback ?? "—"],
                ["Signed in as", user?.method === "google" ? "Google" : "Password"],
              ].map(([l, v], i) => (
                <div key={i} className="rounded-2xl bg-[#122539] border border-[#223851] p-6">
                  <p className="overline text-[#6B7E92]">{l}</p>
                  <p className="font-heading font-extrabold text-3xl text-white mt-2">{v}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-2xl bg-[#122539] border border-[#223851] p-6">
              <p className="overline text-[#6B7E92] mb-4">Plan breakdown</p>
              <div className="space-y-3">
                {stats?.plan_breakdown?.map((p) => (
                  <div key={p.slug} className="flex items-center justify-between">
                    <span className="text-white">{p.name}</span>
                    <span className="font-mono text-[#F26A21]">{p.count} customers</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div data-testid="admin-enquiries" className="rounded-2xl border border-[#223851] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#122539]">
                  <tr>
                    {["Name", "Phone", "Email", "Appliance", "Message", "Date"].map((h) => (
                      <th key={h} className="text-left overline text-[#6B7E92] p-4 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {enquiries.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-[#6B7E92]">No enquiries yet.</td></tr>
                  )}
                  {enquiries.map((e) => (
                    <tr key={e.id} className="border-t border-[#223851] bg-[#0F2236]">
                      <td className="p-4 text-white whitespace-nowrap">{e.name}</td>
                      <td className="p-4 text-[#AEBECD] font-mono whitespace-nowrap">{e.phone}</td>
                      <td className="p-4 text-[#AEBECD] whitespace-nowrap">{e.email}</td>
                      <td className="p-4 text-[#AEBECD] whitespace-nowrap">{e.appliance}</td>
                      <td className="p-4 text-[#AEBECD] max-w-xs truncate">{e.message}</td>
                      <td className="p-4 text-[#6B7E92] font-mono whitespace-nowrap">{(e.created_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "customers" && (
          <div data-testid="admin-customers" className="space-y-4">
            {customers.length === 0 && (
              <div className="rounded-2xl border border-[#223851] bg-[#122539] p-8 text-center text-[#6B7E92]">No customers yet.</div>
            )}
            {customers.map((c) => (
              <CustomerRow key={c.customer_id} customer={c} onChange={refresh} />
            ))}
          </div>
        )}

        {tab === "feedback" && (
          <div data-testid="admin-feedback" className="space-y-4">
            {feedback.length === 0 && (
              <div className="rounded-2xl border border-[#223851] bg-[#122539] p-8 text-center text-[#6B7E92]">No feedback yet.</div>
            )}
            {feedback.map((f) => (
              <div key={f.id} className="rounded-2xl border border-[#223851] bg-[#122539] p-5" data-testid={`admin-feedback-${f.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-heading font-bold text-white">
                    {f.name}
                    {f.customer_id && <span className="font-mono text-sm text-[#F26A21] ml-2">{f.customer_id}</span>}
                  </p>
                  <span className={`text-xs uppercase tracking-wide px-3 py-1 rounded-full border ${
                    f.overall_experience === "Excellent" ? "text-[#3FBF7F] border-[#3FBF7F]/40" :
                    f.overall_experience === "Good" ? "text-[#F26A21] border-[#F26A21]/40" : "text-[#AEBECD] border-[#33506e]"
                  }`}>
                    {f.overall_experience}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4 text-sm">
                  {[
                    ["On time", f.on_time],
                    ["Behaviour", f.engineer_behaviour],
                    ["Explanation", f.engineer_explanation],
                    ["Warranty explained", f.warranty_explained],
                    ["Spare replaced", f.spare_replaced],
                  ].map(([l, v], i) => (
                    <div key={i} className="rounded-lg bg-[#0F2236] border border-[#223851] p-3">
                      <p className="overline text-[#6B7E92]">{l}</p>
                      <p className="text-white mt-1">{v}</p>
                    </div>
                  ))}
                </div>
                {f.remarks && <p className="text-[#AEBECD] mt-4 text-sm italic">"{f.remarks}"</p>}
                <p className="text-xs text-[#6B7E92] font-mono mt-3">{(f.created_at || "").slice(0, 10)}{f.phone ? ` · ${f.phone}` : ""}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "gallery" && (
          <GalleryTab gallery={gallery} onChange={refresh} />
        )}
      </div>
    </div>
  );
}

function GalleryTab({ gallery, onChange }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("General");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const mediaBase = `${process.env.REACT_APP_BACKEND_URL}/api/media`;
  const inputClass = "h-12 bg-[#0C1C2E] border border-[#223851] rounded-lg px-3 text-white text-sm placeholder:text-[#6B7E92] focus:border-[#F26A21] focus:outline-none";

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !title.trim()) { toast.error("Choose a file and enter a title."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      fd.append("caption", caption);
      await adminApi.post("/admin/gallery", fd);
      toast.success("Uploaded to gallery");
      setFile(null); setTitle(""); setCaption(""); setCategory("General");
      e.target.reset?.();
      await onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/admin/gallery/${id}`);
      toast.success("Removed");
      await onChange();
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div data-testid="admin-gallery">
      <form onSubmit={upload} data-testid="gallery-upload-form" className="rounded-2xl bg-[#122539] border border-[#223851] p-6 mb-8">
        <p className="overline text-[#6B7E92] mb-4">Upload repair photo or video</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input type="file" accept="image/*,video/*" data-testid="gallery-file" onChange={(e) => setFile(e.target.files[0])} className="text-sm text-[#AEBECD] file:mr-3 file:rounded-full file:border-0 file:bg-[#F26A21] file:text-white file:px-4 file:py-2 file:font-semibold" />
          <input placeholder="Title (e.g. AC gas charging — Kukatpally)" data-testid="gallery-title" className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-12 bg-[#0C1C2E] border border-[#223851] rounded-lg px-3 text-white text-sm" data-testid="gallery-category"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#122539] border-[#223851] text-white">
              <SelectItem value="General">General</SelectItem>
              {APPLIANCES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <input placeholder="Caption (optional)" data-testid="gallery-caption" className={inputClass} value={caption} onChange={(e) => setCaption(e.target.value)} />
        </div>
        <button type="submit" disabled={uploading} data-testid="gallery-upload-submit" className="mt-4 inline-flex items-center gap-2 bg-[#F26A21] text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-[#DE5C15] transition-colors disabled:opacity-60">
          {uploading ? <Loader2 className="animate-spin" size={16} /> : <><Upload size={16} /> Upload</>}
        </button>
      </form>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {gallery.length === 0 && (
          <div className="col-span-full rounded-2xl border border-[#223851] bg-[#122539] p-8 text-center text-[#6B7E92]">No media uploaded yet.</div>
        )}
        {gallery.map((g) => (
          <div key={g.id} className="rounded-2xl overflow-hidden border border-[#223851] bg-[#122539]" data-testid={`admin-gallery-item-${g.id}`}>
            <div className="aspect-video bg-[#0C1C2E]">
              {g.media_type === "video" ? (
                <video src={`${mediaBase}/${g.storage_path}`} controls preload="metadata" className="w-full h-full object-cover" />
              ) : (
                <img src={`${mediaBase}/${g.storage_path}`} alt={g.title} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="p-4 flex items-start justify-between gap-3">
              <div>
                <p className="overline text-[#F26A21] text-[10px]">{g.category}</p>
                <p className="text-white font-semibold text-sm mt-0.5">{g.title}</p>
              </div>
              <button onClick={() => remove(g.id)} data-testid={`gallery-delete-${g.id}`} className="shrink-0 text-[#6B7E92] hover:text-red-400 transition-colors" aria-label="Delete">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomerRow({ customer, onChange }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [visit, setVisit] = useState({ date: "", type: "", status: "scheduled", note: "" });

  const addVisit = async () => {
    if (!visit.date || !visit.type) { toast.error("Date and type are required"); return; }
    setAdding(true);
    try {
      await adminApi.post(`/admin/customers/${customer.customer_id}/visits`, visit);
      toast.success("Visit added");
      setVisit({ date: "", type: "", status: "scheduled", note: "" });
      await onChange();
    } catch { toast.error("Failed to add visit"); }
    finally { setAdding(false); }
  };

  const markStatus = async (index, status) => {
    try {
      await adminApi.patch(`/admin/customers/${customer.customer_id}/visits/${index}`, { status });
      toast.success("Updated");
      await onChange();
    } catch { toast.error("Failed to update"); }
  };

  const inputClass = "h-11 bg-[#0C1C2E] border border-[#223851] rounded-lg px-3 text-white text-sm placeholder:text-[#6B7E92] focus:border-[#F26A21] focus:outline-none";

  return (
    <div className="rounded-2xl border border-[#223851] bg-[#122539] overflow-hidden" data-testid={`admin-customer-${customer.customer_id}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between p-5 text-left">
        <div>
          <p className="font-heading font-bold text-white">{customer.name} <span className="font-mono text-sm text-[#F26A21] ml-2">{customer.customer_id}</span></p>
          <p className="text-sm text-[#AEBECD] mt-1">{customer.plan_name} · {customer.phone} · {customer.city}</p>
        </div>
        <span className="text-[#6B7E92] text-sm">{open ? "Hide" : "Manage"}</span>
      </button>

      {open && (
        <div className="border-t border-[#223851] p-5">
          {customer.appliances?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {customer.appliances.map((a) => (
                <span key={a} className="text-xs rounded-full border border-[#223851] px-3 py-1 text-[#D3DEE8]">{a}</span>
              ))}
            </div>
          )}

          <p className="overline text-[#6B7E92] mb-2">Service history</p>
          <div className="space-y-2 mb-6">
            {customer.visits?.map((v, i) => (
              <div key={i} className="flex flex-wrap items-center gap-3 justify-between rounded-lg bg-[#0F2236] border border-[#223851] p-3">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-white">{v.date}</span>
                  <span className="text-sm text-white">{v.type}</span>
                  <span className={`text-xs uppercase tracking-wide px-2 py-0.5 rounded-full border ${v.status === "completed" ? "text-[#3FBF7F] border-[#3FBF7F]/40" : v.status === "scheduled" ? "text-[#F26A21] border-[#F26A21]/40" : "text-[#AEBECD] border-[#33506e]"}`}>{v.status}</span>
                </div>
                {v.status !== "completed" && (
                  <button onClick={() => markStatus(i, "completed")} data-testid={`mark-completed-${customer.customer_id}-${i}`} className="inline-flex items-center gap-1 text-xs bg-[#3FBF7F]/15 text-[#3FBF7F] rounded-full px-3 py-1 hover:bg-[#3FBF7F]/25 transition-colors">
                    <Check size={13} /> Mark done
                  </button>
                )}
              </div>
            ))}
          </div>

          <p className="overline text-[#6B7E92] mb-2">Add a visit</p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <input type="date" data-testid={`visit-date-${customer.customer_id}`} className={inputClass} value={visit.date} onChange={(e) => setVisit({ ...visit, date: e.target.value })} />
            <input placeholder="Type (e.g. Gas charging)" data-testid={`visit-type-${customer.customer_id}`} className={inputClass} value={visit.type} onChange={(e) => setVisit({ ...visit, type: e.target.value })} />
            <Select value={visit.status} onValueChange={(v) => setVisit({ ...visit, status: v })}>
              <SelectTrigger className="h-11 bg-[#0C1C2E] border border-[#223851] rounded-lg px-3 text-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#122539] border-[#223851] text-white">
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <button onClick={addVisit} disabled={adding} data-testid={`add-visit-${customer.customer_id}`} className="h-11 bg-[#F26A21] text-white rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-[#DE5C15] transition-colors disabled:opacity-60">
              {adding ? <Loader2 className="animate-spin" size={16} /> : <><Plus size={16} /> Add</>}
            </button>
          </div>
          <input placeholder="Note (optional)" className={`${inputClass} w-full mt-3`} value={visit.note} onChange={(e) => setVisit({ ...visit, note: e.target.value })} />
        </div>
      )}
    </div>
  );
}
